import React, { useState, useMemo, useRef, useCallback } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Layers } from 'lucide-react';
import PrototypeScopeModal from './PrototypeScopeModal';
import { AnimatedValue, CalcCard, CalcEyebrow, ResultRow, PillButton, CalcTitle } from './CalcCard';

/* ---------------------------------------------------------------------------
   Rapid Prototyping scope configurator.

   Deliberately NOT a package-tier picker: there is one base scope and every
   answer either adds nothing or adds price AND delivery days on top of it.
   No hidden complexity multipliers, nothing ever subtracts — the number the
   visitor watches move is the literal sum of what they picked, which is what
   makes the estimate defensible in the follow-up scope call.
--------------------------------------------------------------------------- */

export const BASE_PRICE = 2500;
export const BASE_DAYS = 7;

const ALWAYS_INCLUDED = {
  de: [
    'Coded Browser-Prototyp',
    'Responsive UI',
    'Wiederverwendbare Komponenten',
    'Zugriff auf das Git-Repository',
    'Vollständiger Quellcode',
    'Live-Preview',
    'QA',
    'Übergabe',
    'Basis-Dokumentation',
  ],
  en: [
    'Coded browser prototype',
    'Responsive UI',
    'Reusable components',
    'Git repository access',
    'Complete source code',
    'Live preview',
    'QA',
    'Handover',
    'Basic documentation',
  ],
};

/* Every option carries its own price/day delta. `none: true` marks the
   mutually-exclusive "nothing here" choice inside a multi-select. */
const QUESTIONS = [
  {
    id: 'definition',
    label: { de: 'Produktdefinition', en: 'Product definition' },
    question: { de: 'Wie klar ist das Produkt bereits definiert?', en: 'How clearly is the product already defined?' },
    help: {
      de: 'Je unschärfer die Idee, desto mehr Konzeptarbeit steckt vor der ersten Zeile Code.',
      en: 'The fuzzier the idea, the more concept work sits in front of the first line of code.',
    },
    options: [
      { id: 'defined', label: { de: 'Bereits definiert', en: 'Already defined' }, price: 0, days: 0 },
      {
        id: 'ux-open',
        label: { de: 'Idee klar, UX muss noch entstehen', en: 'Idea clear, UX needs work' },
        price: 600,
        days: 1,
        detail: {
          de: 'UX-Konzept für den gewählten Flow: Informationsarchitektur, Screens und Klickpfad, bevor es an den Code geht.',
          en: 'UX concept for the chosen flow: information architecture, screens and the click path, before any code.',
        },
      },
      {
        id: 'early',
        label: { de: 'Frühe Idee', en: 'Early idea' },
        price: 1500,
        days: 3,
        detail: {
          de: 'Discovery-Sprint vorab: Wir schärfen Idee, Zielgruppe und Kernfunktionen gemeinsam mit dir, bevor UX-Konzept und Code entstehen.',
          en: 'Upfront discovery sprint: we sharpen the idea, target users and core features together with you before UX and code concepting starts.',
        },
      },
    ],
  },
  {
    id: 'scope',
    label: { de: 'Umfang', en: 'Experience scope' },
    question: { de: 'Wie viel Produkt soll der Prototyp abbilden?', en: 'How much product should the prototype cover?' },
    help: {
      de: 'Ein scharf geschnittener Ausschnitt beweist oft mehr als ein halbfertiges Gesamtprodukt.',
      en: 'A sharply cut slice usually proves more than a half-finished whole product.',
    },
    options: [
      { id: 'one-flow', label: { de: 'Ein zentraler Flow', en: 'One key flow' }, price: 0, days: 0 },
      {
        id: 'one-feature',
        label: { de: 'Ein vollständiges Feature', en: 'One complete feature' },
        price: 800,
        days: 2,
        detail: {
          de: 'Ein Feature komplett ausgebaut statt nur angerissen — inklusive Rand- und Fehlerfällen.',
          en: 'One feature built out completely, not just sketched — including edge cases and error states.',
        },
      },
      {
        id: 'connected',
        label: { de: 'Mehrere verbundene Bereiche', en: 'Several connected parts' },
        price: 1800,
        days: 4,
        detail: {
          de: 'Mehrere Bereiche, die tatsächlich zusammenhängen: Navigation, Übergänge und gemeinsamer Zustand zwischen ihnen.',
          en: 'Several areas that actually connect: navigation, transitions and shared state between them.',
        },
      },
      {
        id: 'broad',
        label: { de: 'Breiteres Produktkonzept', en: 'Broader product concept' },
        price: 3000,
        days: 6,
        detail: {
          de: 'Ein Ausschnitt, der wie ein echtes Produkt wirkt — mehrere Kernbereiche, konsistent miteinander verbunden.',
          en: 'A slice that reads like a real product — multiple core areas, consistently connected.',
        },
      },
    ],
  },
  {
    id: 'interaction',
    label: { de: 'Interaktion', en: 'Interaction complexity' },
    question: { de: 'Wie komplex verhält sich das Interface?', en: 'How complex does the interface behave?' },
    help: {
      de: 'Zustände, Rollen und Regeln sind der Unterschied zwischen Klickdummy und echtem Produkt.',
      en: 'States, roles and rules are the difference between a click dummy and a real product.',
    },
    options: [
      { id: 'straightforward', label: { de: 'Geradlinig', en: 'Straightforward' }, price: 0, days: 0 },
      {
        id: 'dynamic',
        label: { de: 'Dynamische Zustände / Filter / Bedingungen', en: 'Dynamic states / filters / conditions' },
        price: 700,
        days: 1,
        detail: {
          de: 'Filter, Bedingungen und wechselnde Zustände, die echt reagieren statt nur auszusehen.',
          en: 'Filters, conditions and changing states that actually react, not just look the part.',
        },
      },
      {
        id: 'complex',
        label: { de: 'Komplexe Logik / Rollen / AI', en: 'Complex logic / roles / AI' },
        price: 1500,
        days: 3,
        detail: {
          de: 'Rollenbasierte Logik oder KI-Verhalten mit spürbar mehr Zuständen — als geskriptete Sequenz simuliert, nicht als echtes Multi-User-Backend.',
          en: 'Role-based logic or AI behaviour with noticeably more states — scripted as a simulated sequence, not a real multi-user backend.',
        },
      },
    ],
  },
  {
    id: 'visual',
    label: { de: 'Visuelle Basis', en: 'Visual foundation' },
    question: { de: 'Worauf baut das Interface visuell auf?', en: 'What does the interface build on visually?' },
    help: {
      de: 'Ein bestehendes Designsystem spart den kompletten visuellen Findungsprozess.',
      en: 'An existing design system saves the entire visual exploration phase.',
    },
    options: [
      { id: 'design-system', label: { de: 'Bestehendes Produkt / Designsystem', en: 'Existing product / design system' }, price: 0, days: 0 },
      {
        id: 'brand-only',
        label: { de: 'Bestehende Marke, neues Produkt-UI', en: 'Existing brand, new product UI' },
        price: 500,
        days: 1,
        detail: {
          de: 'Bestehende Markenwerte auf ein neues Produkt-UI übertragen: Farben, Typografie, Tonalität.',
          en: 'Existing brand values translated into a new product UI: colour, type, tone.',
        },
      },
      {
        id: 'new-direction',
        label: { de: 'Neue visuelle Produktrichtung', en: 'New visual product direction' },
        price: 1000,
        days: 2,
        detail: {
          de: 'Visuelle Richtung von Grund auf: ein eigenständiges UI-System statt Ableitung von etwas Bestehendem.',
          en: 'Visual direction from scratch: a UI system in its own right, not derived from something existing.',
        },
      },
    ],
  },
  {
    id: 'data',
    label: { de: 'Daten', en: 'Data' },
    question: { de: 'Wie umfangreich sollen die Demo-Daten sein?', en: 'How rich should the demo data be?' },
    help: {
      de: 'Der Prototyp läuft immer auf simulierten Daten — kein echtes Backend, keine echte Datenbank. Die Frage ist nur, wie viel Pflege in die Demo-Datensätze fließt.',
      en: 'The prototype always runs on simulated data — no real backend, no real database. The only question is how much care goes into the demo datasets.',
    },
    options: [
      { id: 'mock', label: { de: 'Mock- / lokale Daten', en: 'Mock / local data' }, price: 0, days: 0 },
      {
        id: 'rich-mock',
        label: { de: 'Umfangreiches simuliertes Datenset', en: 'Rich simulated dataset' },
        price: 1000,
        days: 2,
        detail: {
          de: 'Größere, redaktionell gepflegt wirkende Demo-Datensätze für glaubwürdigere Tests — weiterhin ohne echte Datenbank im Hintergrund.',
          en: 'Larger, editorially curated-looking demo datasets for more convincing testing — still without a real database behind it.',
        },
      },
    ],
  },
  {
    id: 'auth',
    label: { de: 'Login-Simulation', en: 'Login simulation' },
    question: { de: 'Soll der Prototyp einen Login zeigen?', en: 'Should the prototype show a login?' },
    help: {
      de: 'Ein simulierter Login reicht für die Story. Echte Nutzerkonten mit echter Zugriffskontrolle baut CRE8NOW erst im finalen Produkt.',
      en: 'A simulated login carries the story. Real accounts with real access control get built in the final product, not the prototype.',
    },
    options: [
      { id: 'none', label: { de: 'Keine', en: 'None' }, price: 0, days: 0 },
      {
        id: 'simulated',
        label: { de: 'Simulierter Login', en: 'Simulated login' },
        price: 600,
        days: 1,
        detail: {
          de: 'Login-Bildschirm und -Flow, der die Story erzählt, ohne echte Nutzerverwaltung dahinter.',
          en: 'A login screen and flow that tells the story, without real user management behind it.',
        },
      },
      {
        id: 'simulated-roles',
        label: { de: 'Simulierte Rollen-Ansichten', en: 'Simulated role views' },
        price: 1200,
        days: 2,
        detail: {
          de: 'Mehrere vorgefertigte Ansichten für unterschiedliche Rollen, im Prototyp umschaltbar — ohne echte Zugriffskontrolle im Hintergrund.',
          en: 'Several pre-built views for different roles, switchable in the prototype — without real access control behind it.',
        },
      },
    ],
  },
  {
    id: 'integrations',
    label: { de: 'Integrationen (simuliert)', en: 'Integrations (simulated)' },
    question: { de: 'Welche Systeme soll der Prototyp andeuten?', en: 'Which systems should the prototype gesture at?' },
    help: {
      de: 'Mehrfachauswahl. Jede Anbindung wirkt im Flow echt, läuft aber auf hinterlegten Demo-Antworten — kein Live-System im Hintergrund.',
      en: 'Multi-select. Every connection feels real in the flow but runs on staged demo responses — no live system behind it.',
    },
    multi: true,
    options: [
      { id: 'none', label: { de: 'Keine Integrationen', en: 'No integrations' }, price: 0, days: 0, none: true },
      {
        id: 'api',
        label: { de: 'Simulierte externe API-Antwort', en: 'Simulated external API response' },
        price: 900,
        days: 2,
        detail: {
          de: 'Eine externe API-Antwort als Demo-Daten fest hinterlegt — wirkt im Flow live, ist aber nicht wirklich verbunden.',
          en: 'An external API response hard-coded as demo data — looks live in the flow, but nothing is actually connected.',
        },
      },
      {
        id: 'payment',
        label: { de: 'Simulierter Checkout-Flow', en: 'Simulated checkout flow' },
        price: 1100,
        days: 2,
        detail: {
          de: 'Zahlungsstrecke als Klickpfad mit Test-UI — keine echte Zahlungsabwicklung im Hintergrund.',
          en: 'Payment flow as a click path with test UI — no real payment processing behind it.',
        },
      },
      {
        id: 'email',
        label: { de: 'Simulierte E-Mail-Benachrichtigung', en: 'Simulated email notification' },
        price: 650,
        days: 1,
        detail: {
          de: 'Der E-Mail-Screen erscheint im Flow als Demo — es wird nichts tatsächlich versendet.',
          en: 'The email screen appears in the flow as a demo — nothing actually gets sent.',
        },
      },
      {
        id: 'maps',
        label: { de: 'Karten- / Standort-Demo', en: 'Maps / location demo' },
        price: 600,
        days: 1,
        detail: {
          de: 'Eine Karten- oder Standortansicht mit hinterlegten Demo-Koordinaten, kein Live-Kartendienst dahinter.',
          en: 'A map or location view with staged demo coordinates — no live mapping service behind it.',
        },
      },
      {
        id: 'cms',
        label: { de: 'Demo-CMS-Inhalte', en: 'Demo CMS content' },
        price: 850,
        days: 2,
        detail: {
          de: 'Inhalte wirken redaktionell gepflegt, liegen aber als strukturierte Demo-Daten vor — kein echtes CMS angebunden.',
          en: 'Content reads as editorially maintained but is structured demo data — no real CMS wired up.',
        },
      },
    ],
  },
  {
    id: 'ai',
    label: { de: 'AI-Integration', en: 'AI integration' },
    question: { de: 'Welche AI-Funktionen soll der Prototyp abbilden?', en: 'Which AI features should the prototype cover?' },
    help: {
      de: 'Mehrfachauswahl. Jede Funktion bindet ein echtes Modell an, kein statisches Mock-Ergebnis.',
      en: 'Multi-select. Every feature wires up a real model, not a static mock result.',
    },
    multi: true,
    options: [
      { id: 'none', label: { de: 'Keine AI-Funktionen', en: 'No AI features' }, price: 0, days: 0, none: true },
      {
        id: 'text',
        label: { de: 'Text-Generierung', en: 'Text generation' },
        price: 600,
        days: 1,
        detail: {
          de: 'Echte Textgenerierung über ein LLM, live im Prototyp.',
          en: 'Real text generation via an LLM, live in the prototype.',
        },
      },
      {
        id: 'image',
        label: { de: 'Bild-Generierung', en: 'Image generation' },
        price: 800,
        days: 2,
        detail: {
          de: 'Bildgenerierung über ein echtes Modell, kein statisches Beispielbild.',
          en: 'Image generation via a real model, not a static sample image.',
        },
      },
      {
        id: 'video',
        label: { de: 'Video-Generierung', en: 'Video generation' },
        price: 1500,
        days: 3,
        detail: {
          de: 'Videogenerierung — das aufwändigste AI-Feature, entsprechend mehr Integrationsaufwand.',
          en: 'Video generation — the heaviest AI feature, with integration effort to match.',
        },
      },
      {
        id: 'chat',
        label: { de: 'Conversational AI / Chatbot', en: 'Conversational AI / chatbot' },
        price: 900,
        days: 2,
        detail: {
          de: 'Ein Chat-Interface mit echtem Modell dahinter, inklusive Gesprächsverlauf.',
          en: 'A chat interface backed by a real model, including conversation history.',
        },
      },
      {
        id: 'voice',
        label: { de: 'Sprache / Voice', en: 'Voice / speech' },
        price: 800,
        days: 2,
        detail: {
          de: 'Sprachein- oder -ausgabe über ein echtes Voice-Modell.',
          en: 'Voice input or output via a real voice model.',
        },
      },
      {
        id: 'personalization',
        label: { de: 'Personalisierung / Empfehlungen', en: 'Personalization / recommendations' },
        price: 700,
        days: 2,
        detail: {
          de: 'Inhalte oder Empfehlungen, die sich pro Nutzer unterscheiden.',
          en: 'Content or recommendations that differ per user.',
        },
      },
      {
        id: 'automation',
        label: { de: 'AI-Agents / Automatisierung', en: 'AI agents / automation' },
        price: 1200,
        days: 3,
        detail: {
          de: 'Ein Agent, der mehrschrittig selbstständig handelt statt nur zu antworten.',
          en: 'An agent that acts across multiple steps on its own, not just responds.',
        },
      },
    ],
  },
  {
    id: 'analytics',
    label: { de: 'Analytics', en: 'Analytics' },
    question: { de: 'Soll der Prototyp messen, was passiert?', en: 'Should the prototype measure what happens?' },
    help: {
      de: 'Ohne Messung bleibt jeder Test eine Meinung.',
      en: 'Without measurement every test stays an opinion.',
    },
    options: [
      { id: 'none', label: { de: 'Keine', en: 'None' }, price: 0, days: 0 },
      {
        id: 'basic',
        label: { de: 'Basis-Analytics', en: 'Basic analytics' },
        price: 400,
        days: 1,
        detail: {
          de: 'Seitenaufrufe und Klicks tracken — die Grundlage für jede Auswertung.',
          en: 'Track page views and clicks — the basis for any evaluation.',
        },
      },
      {
        id: 'product',
        label: { de: 'Produkt-Analytics', en: 'Product analytics' },
        price: 800,
        days: 2,
        detail: {
          de: 'Ereignisbasiertes Tracking entlang des Flows: wo Nutzer abbrechen, was sie tatsächlich tun.',
          en: 'Event-based tracking along the flow: where users drop off, what they actually do.',
        },
      },
      {
        id: 'validation',
        label: { de: 'Analytics + Validierungs-Setup', en: 'Analytics + validation setup' },
        price: 1200,
        days: 2,
        detail: {
          de: 'Analytics plus ein Setup, das aus den Daten belastbare Aussagen für Nutzer-Tests macht.',
          en: 'Analytics plus a setup that turns the data into evidence you can act on in user testing.',
        },
      },
    ],
  },
  {
    id: 'extras',
    label: { de: 'Zusatzfunktionen', en: 'Additional functionality' },
    question: { de: 'Was muss zusätzlich funktionieren?', en: 'What else has to work?' },
    help: {
      de: 'Mehrfachauswahl. Nur auswählen, was die Demo wirklich tragen muss.',
      en: 'Multi-select. Only pick what the demo genuinely has to carry.',
    },
    multi: true,
    options: [
      { id: 'none', label: { de: 'Keine Extras', en: 'No extras' }, price: 0, days: 0, none: true },
      {
        id: 'upload',
        label: { de: 'Datei-Upload', en: 'File upload' },
        price: 400,
        days: 1,
        detail: {
          de: 'Dateien hochladen und im Prototyp weiterverarbeiten.',
          en: 'Files can be uploaded and used within the prototype.',
        },
      },
      {
        id: 'search',
        label: { de: 'Suche / Filter', en: 'Search / filtering' },
        price: 400,
        days: 1,
        detail: {
          de: 'Suche und Filterung über die vorhandenen Inhalte.',
          en: 'Search and filtering across the existing content.',
        },
      },
      {
        id: 'charts',
        label: { de: 'Charts', en: 'Charts' },
        price: 500,
        days: 1,
        detail: {
          de: 'Datenvisualisierung mit echten oder simulierten Werten.',
          en: 'Data visualisation with real or simulated values.',
        },
      },
      {
        id: 'notifications',
        label: { de: 'Benachrichtigungen', en: 'Notifications' },
        price: 400,
        days: 1,
        detail: {
          de: 'In-App- oder Push-Benachrichtigungen im Flow.',
          en: 'In-app or push notifications within the flow.',
        },
      },
      {
        id: 'admin',
        label: { de: 'Admin-Bereich', en: 'Admin area' },
        price: 900,
        days: 2,
        detail: {
          de: 'Ein separater Admin-Bereich zur Verwaltung von Inhalten oder Nutzern.',
          en: 'A separate admin area for managing content or users.',
        },
      },
      {
        id: 'i18n',
        label: { de: 'Mehrere Sprachen', en: 'Multiple languages' },
        price: 500,
        days: 1,
        detail: {
          de: 'Das Interface in mehreren Sprachen, nicht nur übersetzte Strings.',
          en: 'The interface in multiple languages, not just translated strings.',
        },
      },
      {
        id: 'motion',
        label: { de: 'Advanced Motion', en: 'Advanced motion' },
        price: 500,
        days: 1,
        detail: {
          de: 'Choreografierte Übergänge und Mikrointeraktionen statt Standard-Animationen.',
          en: 'Choreographed transitions and micro-interactions beyond default animation.',
        },
      },
      {
        id: 'forms',
        label: { de: 'Komplexe Formulare / Konfigurator', en: 'Complex forms / configurator' },
        price: 700,
        days: 2,
        detail: {
          de: 'Mehrstufige Formulare oder ein Konfigurator mit Validierung.',
          en: 'Multi-step forms or a configurator, with validation.',
        },
      },
    ],
  },
];

const COPY = {
  de: {
    back: 'Zurück',
    next: 'Weiter',
    seeResult: 'Scope ansehen',
    multiHint: 'Mehrfachauswahl möglich',
    estimate: 'Geschätzter Scope',
    price: 'Geschätzter Festpreis',
    delivery: 'Geschätzte Lieferzeit',
    days: 'Werktage',
    base: 'Basis Coded Prototype',
    additions: 'Deine Auswahl',
    included: 'Immer enthalten',
    paymentLabel: 'Zahlung',
    paymentFirst: '70 % vor Projektstart',
    paymentSecond: '30 % vor der Datenübergabe',
    noSelection: 'Noch nichts ausgewählt. Der Basis-Scope steht.',
    resultTitle: 'Dein Coded-Prototype-Scope',
    disclaimer: 'Das ist eine erste Scope-Schätzung. CRE8NOW bestätigt den exakten Umfang, den finalen Festpreis und den Zeitplan vor dem Projektstart.',
    ctaPrimary: 'Scope prüfen lassen',
    edit: 'Ändern',
  },
  en: {
    back: 'Back',
    next: 'Next',
    seeResult: 'See scope',
    multiHint: 'Multi-select',
    estimate: 'Estimated scope',
    price: 'Estimated fixed price',
    delivery: 'Estimated delivery',
    days: 'business days',
    base: 'Base coded prototype',
    additions: 'Your selection',
    included: 'Always included',
    paymentLabel: 'Payment',
    paymentFirst: '70% before the project starts',
    paymentSecond: '30% before the data handover',
    noSelection: 'Nothing selected yet. The base scope stands.',
    resultTitle: 'Your coded prototype scope',
    disclaimer: 'This is an initial scope estimate. CRE8NOW confirms the exact scope, final fixed price and timeline before the project starts.',
    ctaPrimary: 'Review my scope',
    edit: 'Edit',
  },
};

/* Compact stepper: completed questions carry a check, the current one is
   ringed in the accent, the rest stay muted. Answered steps are clickable so
   an earlier decision is always one tap away. */
const Stepper = ({ answers, step, phase, color, lang, onJump }) => {
  const isAnswered = (q) => {
    const a = answers[q.id];
    return q.multi ? Array.isArray(a) && a.length > 0 : Boolean(a);
  };

  return (
    <div className="flex items-center">
      {QUESTIONS.map((q, i) => {
        const answered = isAnswered(q);
        const isCurrent = phase === 'wizard' && i === step;
        const done = answered && !isCurrent;
        const reachable = answered || i <= step;
        return (
          <React.Fragment key={q.id}>
            {i > 0 && (
              <div
                className="flex-1 h-px mx-1 md:mx-1.5"
                style={{ backgroundColor: done || (answered && isCurrent) ? color : 'var(--border-color)' }}
              />
            )}
            <button
              type="button"
              onClick={() => reachable && onJump(i)}
              disabled={!reachable}
              aria-current={isCurrent ? 'step' : undefined}
              title={`${String(i + 1).padStart(2, '0')} ${q.label[lang]}`}
              className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                reachable ? 'cursor-pointer' : 'cursor-default'
              }`}
              style={{
                backgroundColor: done ? color : 'transparent',
                border: `${isCurrent ? 2 : 1}px solid ${done || isCurrent ? color : 'var(--border-color)'}`,
                color: done ? '#ffffff' : isCurrent ? color : undefined,
              }}
            >
              {done ? (
                <Check size={11} strokeWidth={3.5} />
              ) : (
                <span
                  className={`font-mono-swiss text-[9px] md:text-[10px] tabular-nums ${isCurrent ? '' : 'opacity-40'}`}
                >
                  {i + 1}
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* Deliverables read as a wrapped pill set, not a checklist column — they are
   a flat set of facts, not a sequence. They sit under the card, outside the
   configurator itself. */
const IncludedPills = ({ lang, color }) => (
  <div className="flex flex-wrap gap-1.5">
    {ALWAYS_INCLUDED[lang].map((item) => (
      <span
        key={item}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border-color text-text-muted"
      >
        <Check size={12} strokeWidth={3} className="flex-shrink-0" style={{ color }} />
        {item}
      </span>
    ))}
  </div>
);

const SideLabel = ({ children, className = '' }) => (
  <div className={`text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-white/70 ${className}`}>{children}</div>
);

/* Right half of the card: the live estimate while the wizard runs, and the
   final totals plus the one CTA once the result is on screen. */
const EstimateSide = ({ c, color, money, additions, totalPrice, totalDays, isResult, onCta }) => (
  <div className="lg:sticky lg:top-28 flex flex-col">
    <SideLabel className="mb-5">{c.estimate}</SideLabel>
    <ResultRow icon={Layers} title={c.base} sub={`${BASE_DAYS} ${c.days}`} value={money(BASE_PRICE)} />

    <div className="mt-6">
      <SideLabel className="mb-2">{c.additions}</SideLabel>
      {additions.length === 0 ? (
        <div className="text-sm text-white/80 leading-relaxed">{c.noSelection}</div>
      ) : (
        <ul>
          <AnimatePresence initial={false}>
            {additions.map((a) => (
              <Motion.li
                key={`${a.questionId}-${a.optionId}`}
                layout
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
                className="flex items-baseline justify-between gap-4 py-2 border-b border-white/15 last:border-b-0"
              >
                <span className="text-sm font-medium leading-snug">{a.label}</span>
                <span className="text-sm font-bold tabular-nums whitespace-nowrap text-white/90">
                  +{money(a.price)} · +{a.days}d
                </span>
              </Motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>

    <div className="mt-7 pt-7 border-t border-white/25">
      <div className="text-base md:text-lg font-semibold">{c.price}</div>
      <AnimatedValue
        value={totalPrice}
        format={money}
        className="block mt-2 font-black tracking-tight leading-none tabular-nums"
        style={{ fontSize: 'clamp(44px, 4.6vw + 10px, 72px)' }}
      />
      <div className="mt-4 text-sm md:text-[15px] text-white/85 leading-relaxed">
        {c.delivery}:{' '}
        <strong className="font-black text-white">
          <AnimatedValue value={totalDays} /> {c.days}
        </strong>
      </div>
      <div className="mt-2 text-sm text-white/85 leading-relaxed">
        {c.paymentLabel}: {c.paymentFirst} · {c.paymentSecond}
      </div>
    </div>

    {isResult && (
      <div className="mt-8">
        <PillButton onClick={onCta} color={color}>{c.ctaPrimary}</PillButton>
        <div className="mt-5 text-xs text-white/70 leading-relaxed">{c.disclaimer}</div>
      </div>
    )}
  </div>
);

const PrototypeConfigurator = ({ color, lang = 'de' }) => {
  const c = COPY[lang] || COPY.en;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState('wizard'); // wizard | result
  const [isModalOpen, setIsModalOpen] = useState(false);
  const topRef = useRef(null);

  const nf = useMemo(
    () => new Intl.NumberFormat(lang === 'de' ? 'de-DE' : 'en-US', { maximumFractionDigits: 0 }),
    [lang]
  );
  const money = useCallback((n) => `€${nf.format(n)}`, [nf]);

  /* Flat list of everything the visitor actually picked that costs something,
     in question order — this is both the panel's breakdown and the payload the
     lead modal ships. */
  const additions = useMemo(() => {
    const out = [];
    QUESTIONS.forEach((q) => {
      const picked = answers[q.id];
      if (!picked) return;
      const ids = q.multi ? picked : [picked];
      ids.forEach((id) => {
        const opt = q.options.find((o) => o.id === id);
        if (!opt || (opt.price === 0 && opt.days === 0)) return;
        out.push({
          questionId: q.id,
          questionLabel: q.label[lang],
          optionId: opt.id,
          label: opt.label[lang],
          price: opt.price,
          days: opt.days,
        });
      });
    });
    return out;
  }, [answers, lang]);

  const totalPrice = BASE_PRICE + additions.reduce((s, a) => s + a.price, 0);
  const totalDays = BASE_DAYS + additions.reduce((s, a) => s + a.days, 0);

  const current = QUESTIONS[step];
  const currentAnswer = answers[current?.id];
  const canAdvance = current?.multi
    ? Array.isArray(currentAnswer) && currentAnswer.length > 0
    : Boolean(currentAnswer);
  const isLastStep = step === QUESTIONS.length - 1;
  const isResult = phase === 'result';

  const scrollToTop = useCallback(() => {
    // Only pull the viewport back when the configurator's head has scrolled
    // out of reach — otherwise every Next click yanks a page that was fine.
    const el = topRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top > -40) return;
    const target = window.scrollY + top - 100;
    if (window.__lenis) window.__lenis.scrollTo(target, { duration: 0.7 });
    else window.scrollTo({ top: target, behavior: 'smooth' });
  }, []);

  const select = (question, optionId) => {
    setAnswers((prev) => {
      if (!question.multi) return { ...prev, [question.id]: optionId };
      const opt = question.options.find((o) => o.id === optionId);
      const currentList = Array.isArray(prev[question.id]) ? prev[question.id] : [];
      // The "none" option is exclusive in both directions: picking it clears
      // everything else, picking anything else clears it.
      if (opt?.none) return { ...prev, [question.id]: currentList.includes(optionId) ? [] : [optionId] };
      const withoutNone = currentList.filter((id) => !question.options.find((o) => o.id === id)?.none);
      const nextList = withoutNone.includes(optionId)
        ? withoutNone.filter((id) => id !== optionId)
        : [...withoutNone, optionId];
      return { ...prev, [question.id]: nextList };
    });
  };

  const goNext = () => {
    if (!canAdvance) return;
    if (isLastStep) {
      setPhase('result');
      scrollToTop();
      return;
    }
    setStep((s) => s + 1);
    scrollToTop();
  };

  const goBack = () => {
    if (isResult) {
      setPhase('wizard');
      setStep(QUESTIONS.length - 1);
      scrollToTop();
      return;
    }
    if (step === 0) return;
    setStep((s) => s - 1);
    scrollToTop();
  };

  const editQuestion = (index) => {
    setPhase('wizard');
    setStep(index);
    scrollToTop();
  };

  /* Everything the modal needs, assembled once so the mail and the on-screen
     result can never disagree. */
  const scope = useMemo(() => {
    const labelsFor = (questionId) => {
      const q = QUESTIONS.find((item) => item.id === questionId);
      const picked = answers[questionId];
      const ids = q.multi ? (picked || []) : (picked ? [picked] : []);
      return ids
        .map((id) => q.options.find((o) => o.id === id))
        .filter((o) => o && !o.none)
        .map((o) => o.label[lang]);
    };
    const selectionSummary = QUESTIONS.map((q) => {
      const picked = answers[q.id];
      const ids = q.multi ? (picked || []) : (picked ? [picked] : []);
      const labels = ids.map((id) => q.options.find((o) => o.id === id)?.label[lang]).filter(Boolean);
      return `${q.label[lang]}: ${labels.join(', ') || '-'}`;
    }).join(' | ');

    return {
      totalPrice,
      totalDays,
      priceLabel: money(totalPrice),
      additionsCount: additions.length,
      payload: {
        estimatedPrice: money(totalPrice),
        estimatedDelivery: `${totalDays} ${c.days}`,
        basePrice: money(BASE_PRICE),
        baseDays: BASE_DAYS,
        scopeSelections: selectionSummary,
        scopeAdditions: additions.map((a) => `${a.label} (+${money(a.price)} / +${a.days}d)`),
        integrations: labelsFor('integrations'),
        aiFeatures: labelsFor('ai'),
        extras: labelsFor('extras'),
      },
    };
  }, [answers, additions, totalPrice, totalDays, money, lang, c.days]);

  /* ---------- render ---------- */

  const openScopeModal = () => {
    setIsModalOpen(true);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'prototype_scope_review_click', {
        estimated_price: totalPrice,
        estimated_days: totalDays,
      });
    }
  };

  const priceDelta = (opt) => (opt.price === 0 && opt.days === 0
    ? `${money(0)} · +0d`
    : `+${money(opt.price)} · +${opt.days}d`);

  return (
    <div ref={topRef} className="mt-4 md:mt-8">
      <CalcCard
        color={color}
        className="lg:grid-cols-12"
        leftClassName="lg:col-span-7"
        rightClassName="lg:col-span-5"
        left={(
          <>
            {/* The markers carry the whole progress story: cleared, current, ahead. */}
            <div className="pb-7 md:pb-8 mb-7 md:mb-8 border-b border-border-color">
              <Stepper answers={answers} step={step} phase={phase} color={color} lang={lang} onJump={editQuestion} />
            </div>

            <AnimatePresence mode="wait">
              {!isResult ? (
                <Motion.div
                  key={current.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                  <CalcEyebrow className="mb-3">
                    {current.label[lang]}
                    {current.multi && <span className="opacity-60"> / {c.multiHint}</span>}
                  </CalcEyebrow>

                  <CalcTitle className="mb-3">
                    {current.question[lang]}
                  </CalcTitle>
                  <div className="text-sm md:text-[15px] text-text-muted font-light leading-relaxed mb-8">{current.help[lang]}</div>

                  <div className="border-t border-border-color">
                    {current.options.map((opt) => {
                      const selected = current.multi
                        ? Array.isArray(currentAnswer) && currentAnswer.includes(opt.id)
                        : currentAnswer === opt.id;
                      const free = opt.price === 0 && opt.days === 0;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => select(current, opt.id)}
                          aria-pressed={selected}
                          className="group w-full text-left border-b border-border-color py-5 flex items-start gap-4 transition-colors"
                        >
                          <span
                            className="mt-0.5 w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center transition-all duration-200"
                            style={{
                              borderColor: selected ? color : 'var(--border-color)',
                              backgroundColor: selected ? color : 'transparent',
                              borderRadius: current.multi ? '6px' : '999px',
                            }}
                          >
                            {selected && <Check size={11} strokeWidth={4} className="text-white" />}
                          </span>

                          <span className="flex-1 flex flex-col gap-1.5">
                            <span className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                              <span
                                className={`text-[15px] md:text-base font-semibold leading-snug transition-opacity ${
                                  selected ? 'opacity-100' : 'opacity-75 group-hover:opacity-100'
                                }`}
                              >
                                {opt.label[lang]}
                              </span>
                              <span
                                className="text-base md:text-lg font-black tracking-tight tabular-nums whitespace-nowrap"
                                style={{ color: free ? undefined : color, opacity: free ? 0.45 : 1 }}
                              >
                                {priceDelta(opt)}
                              </span>
                            </span>
                            {/* The "why" behind the price delta — without this, a line
                                like "Frühe Idee +€1.500" is just a number nobody can
                                reason about. */}
                            {opt.detail && (
                              <span className="text-[13px] font-light leading-snug text-text-muted">
                                {opt.detail[lang]}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-3 mt-8">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={step === 0}
                      className="group flex items-center gap-2 px-5 py-3 border border-border-color rounded-full font-semibold text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-colors hover:border-text-color/40"
                    >
                      <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
                      {c.back}
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      disabled={!canAdvance}
                      className="group flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white disabled:opacity-30 disabled:cursor-not-allowed transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: color }}
                    >
                      {isLastStep ? c.seeResult : c.next}
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </Motion.div>
              ) : (
                <Motion.div
                  key="result"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                  <CalcEyebrow className="mb-3">{lang === 'de' ? 'Ergebnis' : 'Result'}</CalcEyebrow>
                  <CalcTitle className="mb-8">
                    {c.resultTitle}
                  </CalcTitle>

                  {/* Full selection recap, each row jumping back to its question.
                      The totals sit in the accent panel as the sum of what is listed. */}
                  <div className="border-t border-border-color">
                    {QUESTIONS.map((q, i) => {
                      const picked = answers[q.id];
                      const ids = q.multi ? (picked || []) : (picked ? [picked] : []);
                      const opts = ids.map((id) => q.options.find((o) => o.id === id)).filter(Boolean);
                      return (
                        <div key={q.id} className="border-b border-border-color py-4 flex items-start gap-4">
                          <span className="text-[11px] font-semibold tabular-nums text-text-muted mt-0.5 flex-shrink-0">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1">
                            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-text-muted mb-1">
                              {q.label[lang]}
                            </div>
                            {opts.map((opt) => {
                              const free = opt.price === 0 && opt.days === 0;
                              return (
                                <div key={opt.id} className="flex flex-wrap items-baseline justify-between gap-x-6">
                                  <span className="text-[15px] font-semibold leading-snug">{opt.label[lang]}</span>
                                  <span
                                    className="text-sm md:text-base font-black tracking-tight tabular-nums whitespace-nowrap"
                                    style={{ color: free ? undefined : color, opacity: free ? 0.45 : 1 }}
                                  >
                                    {priceDelta(opt)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={() => editQuestion(i)}
                            className="text-xs font-semibold opacity-60 hover:opacity-100 transition-opacity mt-0.5 flex-shrink-0"
                            style={{ color }}
                          >
                            {c.edit}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </Motion.div>
              )}
            </AnimatePresence>
          </>
        )}
        right={(
          <EstimateSide
            c={c}
            color={color}
            money={money}
            additions={additions}
            totalPrice={totalPrice}
            totalDays={totalDays}
            isResult={isResult}
            onCta={openScopeModal}
          />
        )}
      />

      <div className="mt-8 md:mt-10">
        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-text-muted mb-3">{c.included}</div>
        <IncludedPills lang={lang} color={color} />
      </div>

      <PrototypeScopeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        color={color}
        lang={lang}
        scope={scope}
      />
    </div>
  );
};

export default PrototypeConfigurator;
